import React from 'react'
import { Dropdown, ButtonGroup, Button } from 'react-bootstrap'
import { useTheme } from '../../hooks/useTheme'

/**
 * Toolbar responsive con acciones principales y dropdown para acciones secundarias
 * 
 * Desktop: Todos los botones visibles
 * Móvil: Botones principales (Crear HT + Ver Finalizados) + Dropdown "Más Acciones"
 * 
 * @param {Object} props
 * @param {string} props.task - Tipo de tarea ('finalizar' o 'revisar')
 * @param {Function} props.onCreateHT - Handler crear hoja trabajo
 * @param {Function} props.onOpenHT - Handler abrir modal HT (con tipo: 'Equipos Finalizados' o 'Hojas de Trabajo')
 * @param {Function} props.onAddItems - Handler adicionar ítems
 * @param {Function} props.onAddEquiposProgramacion - Handler adicionar equipos a programación
 * @param {Function} props.onSalidaEntrega - Handler salida patrones
 * @param {Function} props.onConfirmarPrestamo - Handler préstamo
 * @param {Function} props.onGasto - Handler registrar gasto
 * @param {Function} props.onExport - Handler exportar Excel
 * @param {Function} props.onSolicitarFirma - Handler solicitar firma remota
 * @param {boolean} [props.disableCreate=false] - Deshabilitar botón crear
 * @param {number} [props.equiposSeleccionados=0] - Cantidad equipos seleccionados (para revisar)
 */
const ToolbarActionsResponsive = ({ 
  task,
  onCreateHT,
  onAprobar,
  onOpenHT,
  onAddItems,
  onAddEquiposProgramacion,
  onSalidaEntrega,
  onConfirmarPrestamo,
  onGasto,
  onExport,
  onSolicitarFirma,
  disableCreate = false,
  equiposSeleccionados = 0
}) => {
  const { size } = useTheme()
  const isMobile = size?.width < 768
  const isTablet = size?.width >= 768 && size?.width < 992
  
  return (
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2 mb-3">
      
      {/* Acciones principales - Siempre visibles */}
      <ButtonGroup className={isMobile ? 'w-100' : ''}>
        {/* Botón Crear HT (task finalizar) o Aprobar (task revisar) */}
        {task === 'revisar' ? (
          <Button
            variant="success"
            onClick={onAprobar}
            disabled={equiposSeleccionados === 0}
            className={isMobile ? 'w-50' : ''}
          >
            <i className="bi bi-check-circle me-1"/>
            {isMobile ? `Aprobar (${equiposSeleccionados})` : `Aprobar (${equiposSeleccionados})`}
          </Button>
        ) : (
          <Button 
            variant="success"
            onClick={onCreateHT}
            disabled={disableCreate}
            className={isMobile ? 'w-50' : ''}
          >
            <i className="bi bi-file-earmark-text me-1"/>
            {isMobile ? 'Crear HT' : 'Crear Hoja Trabajo'}
          </Button>
        )}
        
        {/* Botón Ver Finalizados */}
        <Button 
          variant="outline-primary"
          onClick={() => onOpenHT('Equipos Finalizados')}
          className={isMobile && task !== 'revisar' ? 'w-50' : ''}
        >
          <i className="bi bi-check-circle me-1"/>
          {isMobile ? 'Finalizados' : 'Ver Finalizados'}
        </Button>
        
      </ButtonGroup>
      
      {/* Acciones secundarias - Dropdown */}
      <div className="d-flex gap-2">
        <Dropdown>
          <Dropdown.Toggle 
            variant="outline-secondary"
            className={isMobile ? 'w-100' : ''}
          >
            <i className="bi bi-three-dots me-1"/>
            Más Acciones
          </Dropdown.Toggle>
          
          <Dropdown.Menu align={isMobile ? 'start' : 'end'}>
            {/* Adicionar Ítems */}
            {task !== 'revisar' && (
              <Dropdown.Item onClick={onAddItems}>
                <i className="bi bi-plus-circle me-2 text-success"/>
                Adicionar Ítems
              </Dropdown.Item>
            )}
            
            {/* Adicionar Equipos a Programación */}
            {task !== 'revisar' && onAddEquiposProgramacion && (
              <Dropdown.Item onClick={onAddEquiposProgramacion}>
                <i className="bi bi-plus-square me-2 text-primary"/>
                Adicionar Equipos a Programación
              </Dropdown.Item>
            )}
            
            {/* Ver Hojas de Trabajo */}
            <Dropdown.Item onClick={() => onOpenHT('Hojas de Trabajo')}>
              <i className="bi bi-file-earmark-text me-2 text-primary"/>
              Ver Hojas de Trabajo
            </Dropdown.Item>

            {/* Ver Hojas Emitidas (Feat C) — hojas inmutables tras finalizar lote */}
            <Dropdown.Item onClick={() => onOpenHT('Hojas Emitidas')}>
              <i className="bi bi-file-earmark-check me-2 text-success"/>
              Ver Hojas Emitidas
            </Dropdown.Item>
            
            {task !== 'revisar' && <Dropdown.Divider />}
            
            {/* Salida Patrones */}
            {task !== 'revisar' && onSalidaEntrega && (
              <Dropdown.Item onClick={onSalidaEntrega}>
                <i className="bi bi-box-arrow-right me-2 text-warning"/>
                Salida Patrones
              </Dropdown.Item>
            )}
            
            {/* Préstamo */}
            {task !== 'revisar' && onConfirmarPrestamo && (
              <Dropdown.Item onClick={() => onConfirmarPrestamo(true)}>
                <i className="bi bi-clipboard-check me-2 text-info"/>
                Confirmar Préstamo
              </Dropdown.Item>
            )}
            
            {/* Registrar Gasto */}
            {task !== 'revisar' && onGasto && (
              <Dropdown.Item onClick={onGasto}>
                <i className="bi bi-currency-dollar me-2 text-success"/>
                Registrar Gasto
              </Dropdown.Item>
            )}
            
            {task !== 'revisar' && <Dropdown.Divider />}
            
            {/* Solicitar Firma Remota */}
            {task !== 'revisar' && onSolicitarFirma && (
              <Dropdown.Item onClick={onSolicitarFirma}>
                <i className="bi bi-pen me-2 text-primary"/>
                Solicitar Firma Remota
              </Dropdown.Item>
            )}
            
            {/* Exportar */}
            {onExport && (
              <>
                <Dropdown.Divider />
                <Dropdown.Item onClick={onExport}>
                  <i className="bi bi-file-earmark-spreadsheet me-2 text-success"/>
                  Exportar a Excel
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
        
      </div>
    </div>
  )
}

export default ToolbarActionsResponsive
